import { useState, FormEvent } from 'react'
import { submitQuoteRequest } from '../firebase/contact'

const SERVICES = [
  { id: 'annual-inspections', label: 'Annual Tank Inspections', unit: 'tank' },
  { id: 'monthly-walkthroughs', label: 'Monthly Walkthroughs', unit: 'site' },
  { id: 'hydrostatic-testing', label: 'Hydrostatic Testing (Spill Buckets & Sumps)', unit: 'site' },
  { id: 'compliance-audits', label: 'Compliance Assistance', unit: 'site' },
  { id: 'overfill-opv', label: 'Overfill Protection Valve (OPV) Testing', unit: 'valve' },
  { id: 'alarm-diagnostics', label: 'Tank Monitor Alarm Diagnostics & Response', unit: 'site' },
  { id: 'tank-maintenance', label: 'Tank Maintenance', unit: 'site' },
]

interface ContactInfo {
  name: string
  email: string
  phone: string
  company: string
  state: string
  notes: string
}

interface FormErrors {
  name?: string
  email?: string
}

const GetQuote = () => {
  const [selectedServices, setSelectedServices] = useState<Record<string, boolean>>({})
  const [quantities, setQuantities] = useState<Record<string, number>>({})

  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    name: '',
    email: '',
    phone: '',
    company: '',
    state: '',
    notes: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const toggleService = (id: string) => {
    setSelectedServices(prev => {
      const next = { ...prev, [id]: !prev[id] }
      if (!next[id]) {
        setQuantities(q => { const n = { ...q }; delete n[id]; return n })
      } else if (quantities[id] === undefined) {
        setQuantities(q => ({ ...q, [id]: 0 }))
      }
      return next
    })
  }

  const updateQuantity = (id: string, value: string) => {
    const num = Math.max(0, Math.min(999, parseInt(value) || 0))
    setQuantities(prev => ({ ...prev, [id]: num }))
  }

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setContactInfo(prev => ({ ...prev, [name]: value }))
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  const hasSelections = Object.values(selectedServices).some(Boolean)

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    if (!contactInfo.name.trim()) newErrors.name = 'Name is required'
    if (!contactInfo.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    if (!hasSelections) {
      setSubmitError('Please select at least one service before submitting.')
      return
    }

    setIsSubmitting(true)
    setSubmitError('')

    try {
      const requestedServices = SERVICES
        .filter(s => selectedServices[s.id])
        .map(s => ({
          service: s.label,
          quantity: quantities[s.id] ?? 0,
          unit: s.unit,
        }))

      await submitQuoteRequest({
        contact: contactInfo,
        services: requestedServices,
      })

      setSubmitSuccess(true)
      setSelectedServices({})
      setQuantities({})
      setContactInfo({ name: '', email: '', phone: '', company: '', state: '', notes: '' })
    } catch (error) {
      console.error('Quote submission error:', error)
      setSubmitError('There was an error submitting your request. Please try again or contact us directly.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-dark via-primary to-primary-light text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundSize: '24px 24px'
          }}></div>
        </div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">Request a Quote</h1>
            <p className="text-xl mb-0 opacity-90">
              Select the services you need, tell us about your facility, and we'll get back to you with a custom quote.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left Column — Service Selection */}
              <div className="lg:w-1/2">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6">Select Services</h2>

                  <div className="space-y-4">
                    {SERVICES.map(service => {
                      const isSelected = selectedServices[service.id]
                      const qty = quantities[service.id] ?? 0
                      const unitLabel = service.unit === 'tank' ? 'Tanks' : service.unit === 'valve' ? 'Valves' : 'Sites'

                      return (
                        <div
                          key={service.id}
                          className={`border rounded-lg p-4 transition-all ${isSelected ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                          <div className="flex items-start gap-4">
                            <input
                              type="checkbox"
                              id={service.id}
                              checked={!!isSelected}
                              onChange={() => toggleService(service.id)}
                              className="mt-1 h-5 w-5 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                            />
                            <div className="flex-grow">
                              <label htmlFor={service.id} className="font-semibold text-lg cursor-pointer block">
                                {service.label}
                              </label>
                            </div>
                            {isSelected && (
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <label htmlFor={`qty-${service.id}`} className="text-sm text-gray-600 whitespace-nowrap">{unitLabel}:</label>
                                <input
                                  type="number"
                                  id={`qty-${service.id}`}
                                  min="0"
                                  max="999"
                                  value={qty}
                                  onChange={(e) => updateQuantity(service.id, e.target.value)}
                                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Service agreement callout */}
                  <div className="mt-6 bg-primary/5 border border-primary/20 rounded-lg px-4 py-3">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-primary-dark"><span className="font-semibold">Ask about our service agreements.</span> Monthly and annual contracts are available for the best value. We'll include options in your quote.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column — Contact Form */}
              <div className="lg:w-1/2">
                <div className="bg-white rounded-lg shadow-lg p-8 sticky top-24">
                  <h2 className="text-2xl font-bold mb-6">Your Information</h2>

                  {submitSuccess ? (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                      <p className="font-medium mb-1">Quote request sent!</p>
                      <p className="text-sm">Thank you for your interest. We'll review your request and get back to you with a custom quote within one business day.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Name */}
                      <div>
                        <label htmlFor="name" className="block text-gray-700 font-medium mb-1 text-sm">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={contactInfo.name}
                          onChange={handleContactChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-colors ${
                            errors.name ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Your name"
                        />
                        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                      </div>

                      {/* Email */}
                      <div>
                        <label htmlFor="email" className="block text-gray-700 font-medium mb-1 text-sm">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={contactInfo.email}
                          onChange={handleContactChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-colors ${
                            errors.email ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="your.email@example.com"
                        />
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                      </div>

                      {/* Phone */}
                      <div>
                        <label htmlFor="phone" className="block text-gray-700 font-medium mb-1 text-sm">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={contactInfo.phone}
                          onChange={handleContactChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
                          placeholder="(123) 456-7890"
                        />
                      </div>

                      {/* Company */}
                      <div>
                        <label htmlFor="company" className="block text-gray-700 font-medium mb-1 text-sm">
                          Company
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={contactInfo.company}
                          onChange={handleContactChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
                          placeholder="Your company"
                        />
                      </div>

                      {/* State */}
                      <div>
                        <label htmlFor="state" className="block text-gray-700 font-medium mb-1 text-sm">
                          State
                        </label>
                        <select
                          id="state"
                          name="state"
                          value={contactInfo.state}
                          onChange={handleContactChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
                        >
                          <option value="">Select a state</option>
                          <option value="GA">Georgia</option>
                          <option value="FL">Florida</option>
                          <option value="SC">South Carolina</option>
                          <option value="NC">North Carolina</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      {/* Notes */}
                      <div>
                        <label htmlFor="notes" className="block text-gray-700 font-medium mb-1 text-sm">
                          Additional Details
                        </label>
                        <textarea
                          id="notes"
                          name="notes"
                          value={contactInfo.notes}
                          onChange={handleContactChange}
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
                          placeholder="Tell us about your facility — number of tanks, types of equipment, any upcoming deadlines, etc."
                        ></textarea>
                      </div>

                      {submitError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                          <p>{submitError}</p>
                        </div>
                      )}

                      <button
                        type="submit"
                        className="btn btn-primary w-full py-3 text-lg font-medium rounded-lg bg-primary text-white hover:bg-primary-dark transition-all"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : 'Request My Quote'}
                      </button>

                      <p className="text-xs text-gray-400 text-center">
                        We'll respond within one business day with a custom quote.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </>
  )
}

export default GetQuote
